import { CrossSmallIcon, GlowingDotIcon } from '@fingertip/icons'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { AssetUploadItemPreview } from '@/components/shared/asset-upload/asset-upload-item-preview'
import { ButtonDiv } from '@/components/ui/button'
import { labelVariants } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { UploaderAsset } from '@/lib/hooks/use-asset-uploader'
import { cn } from '@/lib/utils'
import { fileSrc } from '@/lib/utils/image-loader'

type Props = {
  asset: UploaderAsset
  removeAssetUpload: (assetUpload: UploaderAsset) => void
  size: number
  className?: string
  hidePreview?: boolean
  hideDetails?: boolean
  disabled?: boolean
}

export const AssetUploadItem = ({
  asset,
  className,
  hidePreview,
  size,
  removeAssetUpload,
  hideDetails,
  disabled,
}: Props) => {
  const { t } = useTranslation()

  const handleDownload = useCallback(async () => {
    let blob = await fetch(
      fileSrc({
        bucket: asset?.bucket,
        src: asset?.src,
      }),
    ).then((r) => r.blob())
    const href = URL.createObjectURL(blob)

    // Create a link element to trigger the download
    const link = document.createElement('a')
    link.href = href
    link.download = `${asset?.src}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [asset?.bucket, asset?.src])

  return (
    <div key={asset.id} className="relative mb-4 flex flex-col">
      <div className="flex items-center">
        <div className="relative">
          <div
            className={cn('transition-all duration-200', {
              'opacity-60': disabled,
            })}
          >
            <AssetUploadItemPreview
              loading={asset.status !== 'Completed'}
              className={className}
              asset={asset}
              hidePreview={hidePreview}
              size={size}
            />
          </div>

          <ButtonDiv
            disabled={disabled}
            variant="destructiveSecondary"
            size="xs"
            className="absolute -right-1.5 -top-1.5 z-10 size-[26px] rounded-full bg-background p-0 shadow-lg transition-all hover:shadow-xl"
            type="button"
            onClick={() => removeAssetUpload(asset)}
            title={
              asset.status === 'Uploading'
                ? t('cancel_upload')
                : t('remove_asset')
            }
          >
            <CrossSmallIcon className="size-4" />
          </ButtonDiv>
        </div>

        {!hideDetails && (
          <div className="ml-4 flex-1 flex-col space-y-2">
            <div>
              <div className="line-clamp-1 break-all" title={asset.fileName}>
                <button
                  disabled={disabled}
                  onClick={handleDownload}
                  className={cn(
                    labelVariants(),
                    'text-left cursor-pointer hover:underline block',
                  )}
                >
                  {asset.fileName}
                </button>
              </div>
              {!!asset.new && (
                <div
                  className={cn(
                    'flex flex-row items-center gap-2 leading-[1.5] mt-2',
                  )}
                >
                  <GlowingDotIcon
                    width={12}
                    height={12}
                    className={cn({
                      'text-success': asset.status === 'Completed',
                      'text-destructive': asset.status === 'Failed',
                      'text-warning animate-pulse':
                        asset.status === 'Uploading' ||
                        asset.status === 'Initial',
                    })}
                  />
                  <div
                    className={cn('text-sm font-medium', {
                      'text-success-foreground': asset.status === 'Completed',
                      'text-destructive-foreground': asset.status === 'Failed',
                      'text-warning-foreground':
                        asset.status === 'Uploading' ||
                        asset.status === 'Initial',
                    })}
                  >
                    {asset.status === 'Failed'
                      ? 'Upload failed'
                      : asset.status === 'Completed'
                        ? 'Ready to use'
                        : 'Uploading...'}
                  </div>
                </div>
              )}

              {asset.status === 'Uploading' && (asset?.progress || 0) > 0 && (
                <div className="mt-3 w-full space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      Progress
                    </span>
                    <span className="text-xs font-medium text-primary">
                      {asset.progress?.toFixed(0)}%
                    </span>
                  </div>
                  <Progress
                    value={Math.round(asset?.progress || 0)}
                    className="h-2 w-full"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
